import { TFunction } from "i18next";
import * as Yup from "yup";

import { AddressFieldMaxLength } from "../../../address-fields/AddressFields.constants";
import { ZipCodePattern } from "../../../utils/validationPatterns";
import { ParticipantFormType, ParticipantMaxLength, ParticipantZipCodeMinLength } from "./ParticipantForm.types";

type ParticipantValidationType = {
  [Property in keyof ParticipantFormType]?: Yup.AnySchema | Yup.Reference;
};

export function participantValidationSchema(t: TFunction) {
  return Yup.object().shape<ParticipantValidationType>({
    type: Yup.string()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t("proceedingParticipants.create.type") })),
    name: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("proceedingParticipants.create.name") }))
      .max(ParticipantMaxLength.name, ({ max }) => t("common.validation.maxLength", { max })),
    street: Yup.string().max(AddressFieldMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
    houseNumber: Yup.string().max(AddressFieldMaxLength.houseNumber, ({ max }) =>
      t("common.validation.maxLength", { max })
    ),
    country: Yup.string().max(AddressFieldMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
    zipCode: Yup.string()
      .matches(ZipCodePattern, {
        message: t("proceedingParticipants.create.invalidZipCode"),
        excludeEmptyString: true,
      })
      .test(
        "zip-min-length",
        t("common.validation.minLength", { min: ParticipantZipCodeMinLength }),
        (value) => !value || value.length >= ParticipantZipCodeMinLength
      )
      .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
    city: Yup.string().max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
    note: Yup.string()
      .nullable()
      .max(ParticipantMaxLength.note, ({ max }) => t("common.validation.maxLength", { max })),
  });
}

/**
 * Maps an optional form text value to the nullable request representation: trimmed, or null when
 * blank. Keeps "not set" as NULL only — the same normalisation the consolidation migration applied
 * to the existing rows — so padded input can neither trip the backend length limits nor slip past
 * the ILIKE search.
 */
export function toNullable(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}
